// AddFormPageOne.tsx
import React, {useCallback, useContext, useState} from 'react';
import {
    ProFormDatePicker,
    ProFormDependency,
    ProFormRadio,
    ProFormSelect,
    ProFormText,
    ProFormTreeSelect,
    ProFormUploadDragger,
} from '@ant-design/pro-form';
import axiosApiInstance from '../api/axiosClient';
import ProFormDatePickerYear from '@ant-design/pro-form/es/components/DatePicker/YearPicker';
import type {UploadFile} from 'antd';
import {Image, message, TreeSelect} from 'antd';
import {Store} from 'antd/es/form/interface';
import axios from 'axios';
import {PlusOutlined} from '@ant-design/icons';
import {useAtom} from 'jotai';
import {checksEnabled, fetchAssetLabelOptions, fetchLabelOptions} from './AddEntryForm';
import {DigitalAssetPageContext} from './DigitalAssetsPage';
import {defaultSelectionsAtom, newReleaseUploadQueueAtom,} from '../CRM/pages/DDEXPage/ddex_atoms';
import NewArtistForm from './ArchiveArtists';
import NewLabelForm from '../CRM/pages/DDEXPage/NewLabelForm';
// import NewLabelForm from './ArchiveLabel';
import {countryTreeOptions, genreOptions, genreToSubgenreOptions, releasePriceTierOptions} from '../common/ddex_options'

const fetchArtistOptions = async () =>
    axiosApiInstance
        .get<API.MusicDistribution.Artist[]>(`/api/artists/`)
        .then(({data}) => data)
        .then((artists) =>
            artists.map((artist) => ({
                label: artist.key_name,
                value: artist.key_name,
            }))
        )
        .catch((reason) => {
            console.error(reason);
            return [];
        });

const createArtistDisplayString = (main_artists: string[], featuring_artists: string[]): string => {
    var s = '';
    if (!main_artists || main_artists.length === 0) {
        return s;
    }

    var delim = ', ';
    if (main_artists.length === 2) delim = ' & ';

    s = s + main_artists.join(delim);

    return s;
};

const getBase64 = (file: any): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

const AddFormFirstPage = ({initialValues, edit}: StepsFormFirstPageProps) => {
    const {stepFormRef, firstFormPageRef, secondFormPageRef, listRef, setArtists} =
        useContext(DigitalAssetPageContext);

    const [newReleaseUploadQueue, setNewReleaseUploadQueue] = useAtom(newReleaseUploadQueueAtom);
    const [, setDefaultSelections] = useAtom(defaultSelectionsAtom);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    const {SHOW_PARENT} = TreeSelect;


    const handleFirstPageFlip = async (
        main_artists: API.MusicDistribution.Artist[],
        featuring_artists: API.MusicDistribution.Artist[]
    ) => {
        const tracks = secondFormPageRef?.current?.getFieldValue('art_tracks');
        secondFormPageRef?.current?.setFieldValue(
            'art_tracks',
            tracks.map((track: API.DDEX.Track) => ({...track, main_artists, featuring_artists}))
        );
    };

    const handleTrackNumbers = useCallback(
        (n: number, noOfTracks: number) => {
            if (n > noOfTracks) {
                listRef.current.add(
                    {isrc: '', track_artist_id: firstFormPageRef?.current?.getFieldValue('main_artist_id')},
                    n
                );
            } else {
                listRef.current.remove(n);
            }
        },
        [listRef]
    );

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    const uploadButton = (
        <button style={{border: 0, background: 'none'}} type='button'>
            <PlusOutlined/>
            <div style={{marginTop: 8}}>Upload</div>
        </button>
    );

    return (
        <>
            <ProFormText
                name={'name'}
                label='Release Title'
                placeholder={'Main Title of Release'}
                required
                rules={[
                    {
                        required: checksEnabled,
                    },
                ]}
            />
            <ProFormText
                name={'subtitle'}
                label='Version/Subtitle'
                placeholder={'Release Subtitle (optional)'}
            />
            <ProFormText
                name={'upc_ean'}
                label='UPC/EAN'
                placeholder={`The Release's UPC/EAN Code`}
                required
                rules={[
                    {
                        validator(rule, value, callback) {
                            const upc = value as string;
                            if (typeof upc !== 'string' || upc.length !== 13 || !/^\d{13}$/.test(upc)) {
                                return callback('UPC needs to have 13 numerical digits');
                            }

                            const digits = upc.split('').map(Number);
                            const checkDigit = digits.pop();
                            const sum = digits.reduce((acc, digit, index) => {
                                return acc + (index % 2 === 0 ? digit * 1 : digit * 3);
                            }, 0);
                            const calculatedCheckDigit = (10 - (sum % 10)) % 10;

                            if (calculatedCheckDigit !== checkDigit) {
                                return callback('Invalid UPC. The code you entered does not meet the requirements');
                            }
                            callback();

                        },
                        required: checksEnabled,
                        validateTrigger: 'onFinish',
                    },
                ]}
            />
            <ProFormSelect
                name={'artists'}
                label='Main Artists'
                mode='multiple'
                request={fetchArtistOptions}
                fieldProps={{
                    onChange(value) {
                        const main_artists = value;
                        const featuring_artists = firstFormPageRef?.current?.getFieldValue('featuring_artists');

                        const artist_display = createArtistDisplayString(main_artists, featuring_artists);
                        firstFormPageRef?.current?.setFieldValue('artist_display', artist_display);

                        setDefaultSelections((prev) => ({...prev, main_artists: value}));
                    },
                    dropdownRender(menu) {
                        return (
                            <>
                                {menu}
                                <NewArtistForm stepFormRef={stepFormRef}/>
                            </>
                        );
                    },
                }}
                required
                rules={[
                    {
                        required: checksEnabled,
                    },
                ]}
            />
            <ProFormSelect
                name={'featuring_artists'}
                label='Featuring Artists'
                mode='multiple'
                request={fetchArtistOptions}
                fieldProps={{
                    onChange(value) {
                        const main_artists = value;
                        const featuring_artists = firstFormPageRef?.current?.getFieldValue('featuring_artists');

                        const artist_display = createArtistDisplayString(main_artists, featuring_artists);
                        firstFormPageRef?.current?.setFieldValue('artist_display', artist_display);

                        setDefaultSelections((prev) => ({...prev, featuring_artists: value}));
                    },
                    dropdownRender(menu) {
                        return (
                            <>
                                {menu}
                                <NewArtistForm stepFormRef={stepFormRef}/>
                            </>
                        );
                    },
                }}
            />
            <ProFormSelect
                name={'label_id'}
                label='Label'
                placeholder={`The Release's Record Label`}
                request={fetchLabelOptions}
                fieldProps={{
                    showSearch: true,
                    dropdownRender(menu) {
                        return (
                            <>
                                {menu}
                                <NewLabelForm stepFormRef={stepFormRef}/>
                            </>
                        );
                    },
                }}
                required
                rules={[
                    {
                        required: checksEnabled,
                    },
                ]}
            />
            <ProFormSelect
                name={'asset_label_id'}
                label='Asset Label'
                placeholder={`YouTube Asset Label`}
                request={fetchAssetLabelOptions}
                fieldProps={{
                    showSearch: true,
                }}
            />
            <ProFormSelect
                name='genre'
                label='Genre'
                options={genreOptions}
                showSearch
                fieldProps={{
                    onChange(value, option) {
                        firstFormPageRef?.current?.setFieldValue('sub_genre', undefined);
                    },
                    onClear() {
                        firstFormPageRef?.current?.setFieldValue('sub_genre', undefined);
                    },
                }}
                required
                rules={[
                    {
                        required: checksEnabled,
                    },
                ]}
            />
            <ProFormDependency name={['genre']}>
                {({genre}) => (
                    <ProFormSelect
                        name='sub_genre'
                        label='Subgenre'
                        options={genreToSubgenreOptions[genre]}
                        showSearch
                    />
                )}
            </ProFormDependency>
            <ProFormRadio.Group
                name='type'
                label='Format'
                radioType='button'
                initialValue={'ALBUM'}
                options={['ALBUM', 'SINGLE', 'EP']}
                fieldProps={{
                    onChange(e) {
                        const price_options = releasePriceTierOptions[e.target.value];
                        firstFormPageRef?.current?.setFieldValue('price_tier', price_options[0].value);
                    },
                }}
            />
            <ProFormDatePicker
                name='digital_release_date'
                label='Digital Release Date'
                placeholder={'Release Date'}
                normalize={(value) => value?.format('YYYY-MM-DDTHH:mm:ssZ')}
                fieldProps={{
                    onChange(value, dateString) {
                        firstFormPageRef?.current?.setFieldsValue({
                            original_release_date: value,
                            production_year: value,
                        });
                    },
                }}
                required
                rules={[{required: checksEnabled}]}
            />
            <ProFormDatePicker
                name='original_release_date'
                label='Original Release Date'
                placeholder={'Physical/Original Release Date'}
                normalize={(value) => value?.format('YYYY-MM-DDTHH:mm:ssZ')}
                required
                rules={[{required: checksEnabled}]}
            />
            <ProFormDatePickerYear
                label='Production Year'
                name='production_year'
                normalize={(value) => value?.format('YYYY-MM-DDTHH:mm:ssZ')}
                required
                rules={[{required: checksEnabled}]}
            />
            <ProFormDependency name={['type']}>
                {({type}) => {
                    const options = releasePriceTierOptions[type];
                    return (
                        <ProFormSelect
                            label='Price Tier'
                            name='price_tier'
                            initialValue={options ? options[0].value : undefined}
                            options={options}
                        />
                    );
                }}
            </ProFormDependency>
            <ProFormTreeSelect
                label='Release Territories'
                name='territories'
                initialValue={'Worldwide'}
                request={async () => countryTreeOptions}
                fieldProps={{
                    showCheckedStrategy: SHOW_PARENT,
                    showSearch: true,
                    treeCheckable: true,
                    maxTagCount: 5,
                }}
            />
            <ProFormText hidden name='album_cover_filename'/>
            <ProFormUploadDragger
                title='Cover Art'
                name={'cover_art_file'}
                description={`Drag and drop image file for the release's cover art`}
                fieldProps={{
                    listType: 'picture',
                    maxCount: 1,
                    onPreview: handlePreview,
                    beforeUpload: (file) => {
                        firstFormPageRef?.current?.setFieldValue('album_cover_filename', file.name);
                        return true;
                    },
                    customRequest: async ({file, onSuccess, onError, onProgress}) => {
                        if (!edit) {
                            onSuccess('ok');
                            return;
                        }
                        try {
                            const response = await axiosApiInstance.post(
                                '/api/digitalassets/generate-archiving-url-upload',
                                {
                                    fileName: (file as any).name,
                                    fileType: (file as any).type,
                                    folderName: firstFormPageRef?.current?.getFieldValue('upc_ean'),
                                }
                            );

                            const {presignedUrl} = response.data;

                            return axios
                                .put(presignedUrl, file, {
                                    headers: {
                                        'Content-Type': (file as any).type,
                                    },
                                    onUploadProgress: (progressEvent) => {
                                        const percentCompleted = Math.round(
                                            (progressEvent.loaded * 100) / progressEvent.total
                                        );
                                        onProgress({percent: percentCompleted});
                                    },
                                })
                                .then(() => {
                                    message.success('File uploaded successfully');
                                    onSuccess('ok');
                                })
                                .catch((err) => {
                                    console.error('Error uploading file:', err);
                                    message.error('Error uploading file');
                                    onError(err);
                                });
                        } catch (err) {
                            console.error('Error uploading file:', err);
                            message.error('Error uploading file');
                            onError(err);
                        }
                    },
                }}
                required
                rules={[{required: checksEnabled}]}
            />
            {previewImage && (
                <Image
                    wrapperStyle={{display: 'none'}}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                    }}
                    src={previewImage}
                />
            )}
        </>
    );
};

type StepsFormFirstPageProps = {
    initialValues?: Store;
    edit: boolean;
};

export default AddFormFirstPage;